import { useState, useEffect, useRef } from 'react';
import { getProjectRoot } from '../services/FileSystemService';

// Module-level cache for object URLs
interface CacheEntry {
  url: string;
  refCount: number;
}

const urlCache = new Map<string, CacheEntry>();

function parsePathSegments(path: string): string[] {
  return path
    .replace(/\\/g, '/')
    .split('/')
    .filter(segment => segment.length > 0 && segment !== '.');
}

export async function getFileHandle(relativePath: string): Promise<FileSystemFileHandle | null> {
  const projectRoot = getProjectRoot();
  if (!projectRoot) return null;

  const segments = parsePathSegments(relativePath);
  if (segments.length === 0) return null;

  const fileName = segments.pop()!;
  let currentHandle: FileSystemDirectoryHandle = projectRoot;

  for (const segment of segments) {
    try {
      currentHandle = await currentHandle.getDirectoryHandle(segment);
    } catch {
      return null;
    }
  }

  try {
    return await currentHandle.getFileHandle(fileName);
  } catch {
    return null;
  }
}

export async function getOrCreateObjectUrl(assetPath: string): Promise<string> {
  const cached = urlCache.get(assetPath);
  if (cached) {
    cached.refCount++;
    return cached.url;
  }

  const fileHandle = await getFileHandle(assetPath);
  if (!fileHandle) throw new Error(`File not found: ${assetPath}`);

  const file = await fileHandle.getFile();
  const url = URL.createObjectURL(file);

  urlCache.set(assetPath, { url, refCount: 1 });
  return url;
}

export function releaseObjectUrl(assetPath: string): void {
  const cached = urlCache.get(assetPath);
  if (!cached) return;

  cached.refCount--;
  if (cached.refCount <= 0) {
    URL.revokeObjectURL(cached.url);
    urlCache.delete(assetPath);
  }
}

interface UseAssetUrlResult {
  url: string | null;
  loading: boolean;
  error: Error | null;
}

export function useAssetUrl(assetPath: string | null | undefined): UseAssetUrlResult {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const currentPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (!assetPath) {
      setUrl(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    const cleanup = () => {
      if (currentPathRef.current) {
        releaseObjectUrl(currentPathRef.current);
        currentPathRef.current = null;
      }
    };

    const loadAsset = async () => {
      setLoading(true);
      setError(null);

      try {
        const objectUrl = await getOrCreateObjectUrl(assetPath);

        if (!cancelled) {
          cleanup();
          currentPathRef.current = assetPath;
          setUrl(objectUrl);
          setLoading(false);
        } else {
          releaseObjectUrl(assetPath);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setUrl(null);
          setLoading(false);
        }
      }
    };

    loadAsset();

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [assetPath]);

  return { url, loading, error };
}

export function isImageAssetType(type: string): boolean {
  return type === 'sprite';
}

export function isVideoAssetType(type: string): boolean {
  return type === 'video';
}

export function clearAssetUrlCache(): void {
  for (const [, entry] of urlCache.entries()) {
    URL.revokeObjectURL(entry.url);
  }
  urlCache.clear();
}

export default useAssetUrl;
